"use client";
import embla_carousel_react from "embla-carousel-react";
import { useCallback, useEffect, useId, useMemo, useRef } from "react";
import { useCarouselDotButton } from "./CarouselDotButtonHooks.js";
import { createEmblaPlugins, normalizePlugins } from "./CarouselPluginUtils.js";
import { useCarouselPrevNextButtons } from "./CarouselPrevNextButtonHooks.js";
import { useCarouselAutoplayEffects } from "./useCarouselAutoplayEffects.js";
function createEmblaOptions({ loop, dragFree = false, slidesToScroll, startIndex }) {
    const options = {
        loop,
        startIndex
    };
    if (dragFree) options.dragFree = true;
    if (void 0 !== slidesToScroll) options.slidesToScroll = slidesToScroll;
    return options;
}
function useCarouselEngine({ loop, startIndex, dragFree = false, slidesToScroll, plugins: pluginsProp, carouselIdProp, onSlideChange, prefersReducedMotion }) {
    const generatedId = useId();
    const carouselId = carouselIdProp ?? generatedId;
    const pluginConfigs = normalizePlugins(pluginsProp);
    const hasAutoplay = pluginConfigs.some((c)=>'autoplay' === c.type);
    const autoplayConfig = hasAutoplay ? pluginConfigs.find((c)=>'autoplay' === c.type) : void 0;
    const autoplayMode = autoplayConfig?.mode;
    const autoplayOverride = autoplayConfig?.override;
    const autoplaySyncGlobally = autoplayConfig?.syncGlobally ?? false;
    const emblaOptions = useMemo(()=>createEmblaOptions({
            loop,
            dragFree,
            slidesToScroll,
            startIndex
        }), [
        loop,
        dragFree,
        slidesToScroll,
        startIndex
    ]);
    const pluginsKey = JSON.stringify(pluginConfigs.map((c)=>'autoplay' === c.type ? {
            type: c.type,
            delay: c.delay,
            syncGlobally: c.syncGlobally
        } : c));
    const emblaPlugins = useMemo(()=>createEmblaPlugins(JSON.parse(pluginsKey)), [
        pluginsKey
    ]);
    const [emblaRef, emblaApi] = embla_carousel_react(emblaOptions, emblaPlugins);
    const viewportRef = useRef(null);
    const emblaCallbackRef = useCallback((node)=>{
        viewportRef.current = node;
        emblaRef(node);
    }, [
        emblaRef
    ]);
    const { selectedIndex, onDotButtonClick } = useCarouselDotButton(emblaApi);
    useEffect(()=>{
        if (!emblaApi || !onSlideChange) return;
        const handleSelect = ()=>onSlideChange(emblaApi.selectedScrollSnap());
        emblaApi.on('select', handleSelect);
        return ()=>{
            emblaApi.off('select', handleSelect);
        };
    }, [
        emblaApi,
        onSlideChange
    ]);
    const { prevBtnDisabled, nextBtnDisabled, prevBtnHidden, onPrevButtonClick, onNextButtonClick } = useCarouselPrevNextButtons(emblaApi, loop);
    const { autoplayIsPlaying, toggleAutoplay, resetAutoplayOnInteraction, handleMouseEnter, handleMouseLeave, autoplayInViewRef } = useCarouselAutoplayEffects({
        emblaApi,
        hasAutoplay,
        autoplayMode,
        autoplayOverride,
        autoplaySyncGlobally,
        carouselId,
        prefersReducedMotion
    });
    return {
        carouselId,
        emblaApi,
        emblaCallbackRef,
        viewportRef,
        selectedIndex,
        onDotButtonClick,
        prevBtnDisabled,
        nextBtnDisabled,
        prevBtnHidden,
        onPrevButtonClick,
        onNextButtonClick,
        autoplayIsPlaying,
        toggleAutoplay,
        resetAutoplayOnInteraction,
        handleMouseEnter,
        handleMouseLeave,
        autoplayInViewRef,
        hasAutoplay
    };
}
export { useCarouselEngine };
